import { useQuery } from "@tanstack/react-query";
import { useGraphql } from "~/api-client";
import type { OrderDispatchOption } from "~/types";

export const defaultLanguageStrings = {
  webOrderPickupName: "Pick Up",
  webOrderDeliveryName: "Delivery",
  webOrderServiceName: "Table Service",
  serviceChargeTitle: "Please pay our service charge",
  // Note: body supports markdown
  serviceChargeBody: `100% of all tips, service charges and gratuities go to our staff who in this current climate rely on this more than ever.

Thank you for your support.`,
  serviceChargeApply: "Yes, I'll Pay",
  serviceChargeRemove: "Not Today",
  webChooseLocationTitle: "",
  webServiceRefLabel: "Table Number",
  webPlaceRefLabel: "Table Number",
  webExternalPaymentPickUpButton: "PAY WITH CASH",
  webExternalPaymentPickUpMessage:
    "Cash payments are handled outside of the app. Do you wish to continue?",
  webExternalPaymentDeliveryButton: "PAY WITH CASH",
  webExternalPaymentDeliveryMessage:
    "Cash payments are handled outside of the app. Do you wish to continue?",
  webExternalPaymentServiceButton: "PAY WITH CASH",
  webExternalPaymentServiceMessage:
    "Cash payments are handled outside of the app. Do you wish to continue?",
};

export interface LanguageStrings {
  webOrderPickupName: string | null;
  webOrderDeliveryName: string | null;
  webOrderServiceName: string | null;
  serviceChargeTitle: string;
  serviceChargeBody: string;
  serviceChargeApply: string;
  serviceChargeRemove: string;
  webChooseLocationTitle: string | null;
  webServiceRefLabel: string;
  webPlaceRefLabel: string;
  webExternalPaymentPickUpButton: string;
  webExternalPaymentPickUpMessage: string;
  webExternalPaymentDeliveryButton: string;
  webExternalPaymentDeliveryMessage: string;
  webExternalPaymentServiceButton: string;
  webExternalPaymentServiceMessage: string;
}

export function useDispatchTypeLabel(
  dispatchOption: OrderDispatchOption | "STORE" | null,
  options?: {
    revalidateOnMount?: boolean;
  },
): { label: string | null; loading: boolean } {
  // NOTE: revalidateOnMount can't be easily utilized yet
  // because this useDispatchTypeLabel hook is called in many places
  const { label, loading } = useLanguageStrings(options);

  // Note: while language is loading or if it didn't load you will see the default strings
  // There will always be labels returned
  // Use the loading state if you're worried defaults being seen
  switch (dispatchOption) {
    case "PICK_UP":
      return {
        label: label.webOrderPickupName,
        loading,
      };
    case "DELIVERY":
      return {
        label: label.webOrderDeliveryName,
        loading,
      };

    case "SERVICE":
      return {
        label: label.webOrderServiceName,
        loading,
      };

    case "STORE":
      return {
        label: "Store",
        loading,
      };
    default:
      return { label: null, loading };
  }
}

export function useLanguageStrings(options?: {
  revalidateOnMount?: boolean;
  revalidateOnFocus?: boolean;
}) {
  const { getLanguageStrings } = useGraphql();
  const { data, error, isFetching } = useQuery({
    queryKey: ["language"],
    queryFn: getLanguageStrings,
    refetchOnWindowFocus: options?.revalidateOnFocus ?? false,
    refetchOnMount: options?.revalidateOnMount ?? true,
    staleTime: 20000,
  });

  if (!data || error || !data.strings) {
    return { label: defaultLanguageStrings, loading: isFetching };
  }

  const {
    webChooseLocationTitle = null,
    webOrderDeliveryName = null,
    webOrderPickupName = null,
    webOrderServiceName = null,
    ...rest
  } = data.strings;

  return {
    loading: isFetching,
    label: {
      webChooseLocationTitle,
      webOrderDeliveryName:
        webOrderDeliveryName || defaultLanguageStrings.webOrderDeliveryName,
      webOrderPickupName:
        webOrderPickupName || defaultLanguageStrings.webOrderPickupName,
      webOrderServiceName:
        webOrderServiceName || defaultLanguageStrings.webOrderServiceName,
      ...rest,
    },
  };
}
